import { Fragment, useEffect, useState, useRef, useMemo, memo } from 'react';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import Router from 'next/router';

import getProductUrl from '@JohnLewisPartnership/jl-design-system/utils/product/getProductUrl';
import { Message } from '@JohnLewisPartnership/jl-design-system/components/message/Message';
import { LoadingSpinner } from '@JohnLewisPartnership/jl-design-system/components/loading-spinner/LoadingSpinner';
import Head from 'next/head';
import getConfig from 'next/config';
import {
  dispatchImmediateAnalyticsEvent,
  CHUNK_LOADED,
} from '../analytics/analyticsEvents';
import { calculateProductOffsetByChunk } from '../../utils/analytics/vertexProductOffset';
import ProductCount from './product-count/ProductCount';
import StructuredData from '../structured-data/StructuredData';
import constants from '../../utils/constants';

import styles from './ProductGrid.module.scss';
import { mapProduct } from '../analytics/utils/mapProduct';
import {
  dispatchMonetateManagedImpressions,
  getFilteredExperiencesByKey,
} from '../experiences/Experiences';
import { getProductChunks } from '../../api/products/product-chunks';
import ProductCardContainer from './ProductCardContainer';
import { storageCanBeSet } from '../../utils/storage';
import { CreditBanner } from './credit-banner/CreditBanner';
import { useProductGridData } from './data/useProductGridData';
import { calculateColumnsPerRow } from './utils/calculateColumnsPerRow';

const Modal = dynamic(
  () =>
    import(
      /* webpackChunkName: "modal" */
      '@JohnLewisPartnership/jl-design-system/components/modal/Modal'
    ).then((module) => module.Modal),
  {
    ssr: false,
  }
);

const { publicRuntimeConfig } = getConfig();

const {
  COMPARE_LOCAL_STORAGE_KEY,
  PLP_VIEW_TYPES: { GRID, LIST },
  STORAGE_TYPES: { LOCAL_STORAGE },
} = constants;

export const getIndexOfLastProductInRow = (
  currentIndex,
  totalNumberOfProducts,
  numberOfColumnsPerRow
) => {
  if (currentIndex < 0) {
    return -1;
  }

  const position = currentIndex + 1;
  const currentRow = Math.ceil(position / numberOfColumnsPerRow);
  const lastGridPositionInRow = currentRow * numberOfColumnsPerRow;
  const lastIndex =
    lastGridPositionInRow > totalNumberOfProducts
      ? totalNumberOfProducts
      : lastGridPositionInRow;

  return lastIndex - 1;
};

export const ProductGrid = ({
  handleQuickViewClick = () => {},
  handleDisplayM2MProductSelector,
  handleDisplayQuickAdd,
  isChunkLoadError,
  isLoadingChunk,
  isLoadingPage,
  pageSize = publicRuntimeConfig.pageSize,
  setIsChunkLoadError,
  setIsLoadingChunk,
}) => {
  const {
    compare,
    experiencesContext,
    imageSize,
    imageWidths,
    isLargeCardSize,
    isListView,
    openEnergyDrawer,
    pageCorrelationId,
    personalisedPlp,
    products,
    router,
    setProductContext,
    socialProofingMessages,
    totalNumberOfResults,
  } = useProductGridData();

  const {
    experiences,
    isChunkSizeExperiment,
    isCuratedCategoriesFlagOn,
    monetateId,
    isSingleColumnLayout,
  } = experiencesContext;

  const {
    query: {
      chunk: chunkNumber = 1,
      facetId,
      listHead,
      lolcode,
      page = 1,
      price,
      priceBands,
      'search-term': searchTerm,
      sortBy = '',
      type,
    },
  } = router;
  const pageRef = useRef(page);
  const productGridRef = useRef(null);

  const THRESHOLD = 1800; // pixels

  const [chunkLoadingSentinelInView, setChunkLoadingSentinelInView] =
    useState(false);
  const [addToBasketError, setAddToBasketError] = useState(null);
  const [isMobileBreakpoint, setIsMobileBreakpoint] = useState(false);
  const [currentBreakpoint, setCurrentBreakpoint] = useState('desktop');
  const [isLocalStorageAvailable, setIsLocalStorageAvailable] = useState(true);
  const [creditType, setCreditType] = useState(null);

  const { chunkSize: chunksize, chunkSizeExperiment } = publicRuntimeConfig;
  const chunkSize = isChunkSizeExperiment ? chunkSizeExperiment : chunksize;
  const chunk =
    isChunkSizeExperiment && chunkSize === pageSize ? 1 : chunkNumber;

  const productsToCurrentChunk = useMemo(() => {
    if (
      chunk == 8 ||
      (products.length > totalNumberOfResults &&
        totalNumberOfResults % chunkSize === 0) ||
      (isChunkSizeExperiment && chunkSize === pageSize)
    ) {
      return products;
    } else {
      return products.slice(0, chunk * chunkSize);
    }
  }, [products]);

  const creditBannerGridPositions = useMemo(() => {
    let gridPositions = [];
    const numberOfColumnsPerRow = calculateColumnsPerRow({
      currentBreakpoint,
      isSingleColumnLayout,
      isLargeCardSize,
    });

    const firstIFCIndex = productsToCurrentChunk.findIndex(
      (prod) => prod.credit?.illustration?.financialProductType === 'IFC'
    );
    const indexOfEndOfFirstIFCRow = getIndexOfLastProductInRow(
      firstIFCIndex,
      productsToCurrentChunk.length,
      numberOfColumnsPerRow
    );

    const firstIBCIndex = productsToCurrentChunk.findIndex(
      (prod) => prod.credit?.illustration?.financialProductType === 'IBC'
    );
    const indexOfEndOfFirstIBCRow = getIndexOfLastProductInRow(
      firstIBCIndex,
      productsToCurrentChunk.length,
      numberOfColumnsPerRow
    );

    if (indexOfEndOfFirstIBCRow >= 0 && indexOfEndOfFirstIFCRow >= 0) {
      gridPositions.push(indexOfEndOfFirstIBCRow);
      if (indexOfEndOfFirstIBCRow !== indexOfEndOfFirstIFCRow) {
        gridPositions.push(indexOfEndOfFirstIFCRow);
      }
      setCreditType('ifc-ibc');
    } else if (indexOfEndOfFirstIBCRow >= 0) {
      gridPositions.push(indexOfEndOfFirstIBCRow);
      setCreditType('ibc');
    } else if (indexOfEndOfFirstIFCRow >= 0) {
      gridPositions.push(indexOfEndOfFirstIFCRow);
      setCreditType('ifc');
    }

    return gridPositions;
  }, [
    productsToCurrentChunk,
    currentBreakpoint,
    isLargeCardSize,
    isSingleColumnLayout,
  ]);

  const clearAddToBasketError = () => {
    setAddToBasketError(null);
  };

  const handleAddToBasketClick = () => {
    return Promise.resolve();
  };

  const getBreakpoint = () => {
    switch (true) {
      case window.innerWidth < constants.XS_MOBILE_MAX_WIDTH: // < 568
        return 'xxs';
      case window.innerWidth >= constants.XS_MOBILE_MAX_WIDTH &&
        window.innerWidth < constants.MOBILE_MAX_WIDTH: // >= 568 && < 768
        return 'xs';
      case window.innerWidth >= constants.MOBILE_MAX_WIDTH && // >= 768 && <= 1023
        window.innerWidth <= constants.TABLET_MAX_WIDTH:
        return 'small';
      default:
        return 'medium';
    }
  };

  const handleResize = () => {
    const isNewMobileBreakpoint =
      window.innerWidth < constants.MOBILE_MAX_WIDTH;
    if (isMobileBreakpoint !== isNewMobileBreakpoint) {
      setIsMobileBreakpoint(isNewMobileBreakpoint);
    }

    const newBreakpoint = getBreakpoint();
    if (currentBreakpoint !== newBreakpoint) {
      setCurrentBreakpoint(newBreakpoint);
    }
  };

  const setChunkNumber = (newChunkNumber) => {
    Router.replace(
      { query: { ...router.query, chunk: newChunkNumber } },
      null,
      {
        shallow: true,
        scroll: false,
      }
    );
  };
  const handleChunkLoadAnalytics = (chunkNum, chunkProducts, vertex) => {
    const numberOfProductsToCurrentChunk = (chunkNum - 1) * chunkSize;
    const numberOfAdditionalSponsoredProducts =
      products.length - numberOfProductsToCurrentChunk;

    const productOffset = calculateProductOffsetByChunk(
      page,
      chunkNum,
      chunkSize,
      pageSize
    );

    dispatchImmediateAnalyticsEvent(CHUNK_LOADED, {
      chunkDetails: {
        chunkOffset: chunkNum - 1,
        productOffset,
        vertexAttributionToken: vertex?.attributionToken,
        products: chunkProducts.map((product, i) =>
          mapProduct(
            product,
            numberOfProductsToCurrentChunk +
              numberOfAdditionalSponsoredProducts +
              i
          )
        ),
        personalisedPlp,
      },
    });
  };

  const filteredSocialProofPageExperiences = getFilteredExperiencesByKey(
    experiences,
    'fragment',
    ['social-proof-messaging']
  );

  useEffect(() => {
    const storageType = LOCAL_STORAGE;
    const storageKey = COMPARE_LOCAL_STORAGE_KEY;
    setIsLocalStorageAvailable(
      storageCanBeSet({
        storageType,
        storageKey,
      })
    );
  }, []);

  useEffect(() => {
    let didCancel = false;

    if (page !== pageRef.current) {
      // There was a issue where the PLP was scrolling to the bottom, and not displaying all chunks,
      // when the back button was used to go from PLP to PLP.  This is because the browser scroll restore
      // happens before the product cards are rendered. This scroll is a lesser evil.(PLP-1059)
      window.scrollTo(0, 0);

      pageRef.current = page;
      // do not load chunks if this is running as a result of a page change (eg. back button)
      return;
    }

    if (
      !(chunkSize === pageSize) &&
      chunk * chunkSize > products.length &&
      (page - 1) * pageSize + products.length < totalNumberOfResults
    ) {
      setIsLoadingChunk(true);
      getProductChunks(
        {
          chunk,
          facetId,
          listHead,
          lolcode,
          page,
          price,
          priceBands,
          sortBy,
          term: searchTerm,
          type,
        },
        pageCorrelationId
      )
        .then(({ products, vertex }) => {
          setIsLoadingChunk(false);
          if (!didCancel) {
            setProductContext(products);
            handleChunkLoadAnalytics(chunk, products, vertex);
          }
        })
        .catch(() => {
          setIsLoadingChunk(false);
          setIsChunkLoadError(true);
          setChunkNumber(chunk - 1);
        });
    }

    return () => {
      didCancel = true;
    };
  }, [chunk, page]);

  useEffect(() => {
    if (personalisedPlp) {
      dispatchMonetateManagedImpressions({
        monetateId,
        experiences,
        monetateFlagName: 'discoImpressionsEnabled',
      });
    }
  }, [personalisedPlp]);

  const handleScrollForChunking = () => {
    if (isLoadingChunk) {
      return;
    }

    const sentinelElement = productGridRef.current;
    const sentinelBottom = sentinelElement.getBoundingClientRect().bottom;

    setChunkLoadingSentinelInView(
      sentinelBottom < THRESHOLD + window.innerHeight
    );
  };

  useEffect(() => {
    let didCancel = false;

    if (
      chunkLoadingSentinelInView &&
      !isLoadingPage &&
      !isLoadingChunk &&
      !isChunkLoadError &&
      !(chunkSize === pageSize)
    ) {
      const nextChunk = Number(chunk) + 1;

      if (
        // have reached the end of this page?
        nextChunk * chunkSize > pageSize ||
        // have we reached the end of the whole result set?
        (page - 1) * pageSize + chunk * chunkSize >= totalNumberOfResults
      ) {
        return;
      }

      if (!didCancel) {
        setChunkNumber(nextChunk);
      }
    }

    return () => {
      didCancel = true;
    };
  }, [chunkLoadingSentinelInView]);

  useEffect(() => {
    handleScrollForChunking(true);
    window.addEventListener('scroll', handleScrollForChunking);

    setIsMobileBreakpoint(window.innerWidth <= constants.MOBILE_MAX_WIDTH);

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScrollForChunking);
    };
  }, []);

  const handleManualChunkLoad = () => {
    setIsChunkLoadError(false);
    setChunkNumber(Number(chunk) + 1);
  };

  const getImagePreload = (imageUrl, imageSize, imageWidths) => {
    const srcSetArr = imageWidths.map(
      (width) =>
        `${imageUrl.split('?')[0]}?wid=${width}&hei=${Math.round(
          width / 0.75
        )} ${width}w`
    );

    return (
      <link
        rel="preload"
        as="image"
        fetchpriority="high"
        href={srcSetArr[0].split(' ')[0]}
        imageSrcSet={srcSetArr.join(', ')}
        imageSizes={imageSize}
      />
    );
  };

  const AllProductCards = useMemo(() => {
    return productsToCurrentChunk.map((product, index) => {
      const { image, type } = product;

      const pricePrefix =
        type === 'm2mRollerBlind' ? 'Blind price from' : undefined;

      const isLastProductOnPage = index + 1 === publicRuntimeConfig.pageSize;
      const isMultipleOfViewedProductFrequency = isChunkSizeExperiment
        ? (index + 1) % 24 === 0
        : (index + 1) % chunkSize === 0;
      const shouldShowProductCount =
        !isLastProductOnPage && isMultipleOfViewedProductFrequency;

      const creditBannerIndex = creditBannerGridPositions.indexOf(index);
      const shouldShowCreditBanner = creditBannerIndex > -1;

      // N.B the Fragment key below is deliberately set to undefined to fix the issue where products were being displayed in
      // the wrong order when you changed pages after chunk-loading.  Ignore the duplicate-key warning for this fragment.
      return (
        <Fragment key={'undefined'}>
          {index === 0 && (
            <Head>{getImagePreload(image, imageSize, imageWidths)}</Head>
          )}

          <ProductCardContainer
            handleQuickViewClick={handleQuickViewClick}
            handleAddToBasketClick={handleAddToBasketClick}
            handleDisplayM2MProductSelector={handleDisplayM2MProductSelector}
            handleDisplayQuickAdd={handleDisplayQuickAdd}
            imageSize={imageSize}
            imageWidths={imageWidths}
            index={index}
            isLargeCardSize={isLargeCardSize}
            isMobileBreakpoint={isMobileBreakpoint}
            openEnergyDrawer={openEnergyDrawer}
            pricePrefix={pricePrefix}
            product={product}
            socialProofingMessages={socialProofingMessages}
            socialProofMessageExperiments={filteredSocialProofPageExperiences}
            isLocalStorageAvailable={isLocalStorageAvailable}
            monetateId={monetateId}
          />

          {shouldShowCreditBanner &&
            creditType &&
            !isCuratedCategoriesFlagOn && (
              <CreditBanner
                creditType={creditType}
                bannerIndex={creditBannerIndex}
              />
            )}

          {shouldShowProductCount && (
            <div
              data-testid="component-grid-column"
              className={classNames(styles['product-count'], {
                [styles['product-count--list-view']]: isListView,
              })}
            >
              <ProductCount positionOnPage={index} isListView={isListView} />
            </div>
          )}
        </Fragment>
      );
    });
  }, [
    products,
    isLargeCardSize,
    compare,
    socialProofingMessages,
    isLocalStorageAvailable,
  ]);

  const structuredData = (
    <StructuredData>
      {() => ({
        '@type': 'ItemList',
        '@context': 'https://schema.org',
        numberOfItems: `(${products.length})`,
        itemListElement: products.map((product, i) => ({
          '@type': 'ListItem',
          url: `https://www.johnlewis.com${getProductUrl(
            product.title,
            product.id
          )}`,
          position: i + 1,
        })),
      })}
    </StructuredData>
  );

  return (
    <Fragment>
      <div
        ref={productGridRef}
        data-testid="component-grid-container"
        data-test-id="product-grid"
        data-monetate="disruptor-block-identifier"
        data-plp-view={isListView ? LIST : GRID}
        className={classNames(styles['product-grid'], {
          [styles['product-grid--singleColumn']]: isSingleColumnLayout,
        })}
      >
        <meta name="total-number-of-items" content={products.length} />
        {structuredData}
        {AllProductCards}
      </div>
      {isLoadingChunk && (
        <div className={styles.chunkLoadingSpinner}>
          <LoadingSpinner />
        </div>
      )}
      {isChunkLoadError && (
        <Message
          type="error"
          title="There has been an error, please try again."
          buttonText="Load more products"
          buttonProps={{
            onClick: handleManualChunkLoad,
          }}
          className={styles.chunkLoadErrorMessage}
        />
      )}
      {addToBasketError && (
        <Modal
          footer={false}
          isOpen
          title="Something went wrong"
          onClose={clearAddToBasketError}
        >
          <Message title={addToBasketError.message} type="error" />
        </Modal>
      )}
    </Fragment>
  );
};

const comparisonFn = (prevProps, nextProps) => {
  return nextProps.isLoadingPage === true;
};
export default memo(ProductGrid, comparisonFn);

ProductGrid.displayName = 'ProductGrid';

ProductGrid.propTypes = {
  category: PropTypes.string,
  handleQuickViewClick: PropTypes.func,
  pageSize: PropTypes.number,
  products: PropTypes.instanceOf(Array),
};
